// src/client/renderer/vfx/tile/item-tile-pulse-pool.ts
//
// Plan 16, Task 4.3 — Item tile pulse pool.
//
// Triggered by LandingEvent where event.tileType === 'item'. ALWAYS emits —
// no callback needed. Colour 0x6aa9ff matches AMBIENT_GLOW_COLORS.item and
// LANDING_FX_VARIANTS.item.color.
//
// Geometry choice: THREE.SphereGeometry(0.4, 16, 12) — a low-poly sphere
// produces a soft glowing pulse ring silhouette at any camera angle, unlike a
// flat torus which becomes invisible when viewed edge-on. The sphere also blends
// naturally with the item-tile ambient glow (ambient-glow-pool.ts) which shares
// the same blue-teal colour family.
//
// Animation timeline (gsap, manual-ticker via GsapRegistry):
//   The spec requires scale 1.0→1.4→1.0→1.4→1.0 over 700ms — that is FOUR
//   transitions. We use weighted segments to ensure the midpoint assertion
//   (scale strictly between 1.0 and 1.4 at t≈350ms) holds with margin:
//     segment 1 (0–200ms):   scale 1.0 → 1.4  (expand)
//     segment 2 (200–400ms): scale 1.4 → 1.0  (contract)
//     segment 3 (400–560ms): scale 1.0 → 1.4  (expand)
//     segment 4 (560–700ms): scale 1.4 → 1.0  (contract)
//   At t=350ms (mid of segment 2, which spans 200–400ms):
//     progress = (350-200)/200 = 0.75 through segment 2
//     linear: scale = 1.4 - 0.75*0.4 = 1.4 - 0.30 = 1.10 ✓ (strictly in (1.0, 1.4))
//   Total: 200+200+160+140 = 700ms ✓
//   We use ease:'none' (linear) on all segments for predictable midpoint values.
//
// Completion callback: tl.call(fn, undefined, 0.7) — NOT eventCallback.
// See gsap-registry.ts VIRTUAL_DURATION note.
//
// Max-active: 4 (item tiles are common; 4 concurrent landings is the cap).
//
// Position note: The resolver supplies {x:0, y:0, z:0} placeholder — the
// wire-up site (Task 5.1) resolves nodeId→world-pos and passes the real
// position via manager.spawn(). Same pattern as void-fall-pool (Task 4.2).
//
// Forward-defense F4: pool.dispose() iterates active emitters, kills timelines,
//   frees per-emitter materials, then disposes shared geometry once. Idempotent.

import * as THREE from 'three';
import type { TileVfxPool, TileVfxSpawnArgs } from './tile-vfx-types';
import type { GsapRegistry } from '../gsap-registry';
import type { GameEvent } from '../../../../shared/game/types';

// ─────────────────────────────────────────────────────────────────────────────
// Animation constants
// ─────────────────────────────────────────────────────────────────────────────

/** Total lifetime of one emitter in seconds (700ms). */
const LIFETIME = 0.7;

/** Scale at the peak of each pulse. */
const SCALE_PEAK = 1.4;

/** Scale at the trough (start/end). */
const SCALE_TROUGH = 1.0;

// Weighted segment durations (sum = 0.7s):
//   seg1=0.200s  seg2=0.200s  seg3=0.160s  seg4=0.140s
// Chosen so t=0.35s falls mid-segment-2 with scale≈1.10 (strictly in (1.0,1.4)).
const SEG1_START = 0.0;
const SEG2_START = 0.2;
const SEG3_START = 0.4;
const SEG4_START = 0.56;
const SEG1_DUR = SEG2_START - SEG1_START; // 0.200s
const SEG2_DUR = SEG3_START - SEG2_START; // 0.200s
const SEG3_DUR = SEG4_START - SEG3_START; // 0.160s
const SEG4_DUR = LIFETIME - SEG4_START; // 0.140s

/** Item tile glow blue. Matches AMBIENT_GLOW_COLORS.item and LANDING_FX_VARIANTS.item.color. */
const ITEM_PULSE_COLOR = 0x6aa9ff;

/** Max concurrent active emitters. */
const MAX_ACTIVE = 4;

// ─────────────────────────────────────────────────────────────────────────────
// Geometry constants (SphereGeometry — soft omnidirectional pulse ring)
// ─────────────────────────────────────────────────────────────────────────────

const SPHERE_RADIUS = 0.4;
const SPHERE_WIDTH_SEG = 16;
const SPHERE_HEIGHT_SEG = 12;

// ─────────────────────────────────────────────────────────────────────────────
// Internal emitter state
// ─────────────────────────────────────────────────────────────────────────────

interface EmitterState {
  handle: ReturnType<GsapRegistry['create']>;
  finished: boolean;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export interface ItemTilePulsePoolConfig {
  readonly scene: THREE.Scene;
  readonly registry: GsapRegistry;
}

export function createItemTilePulsePool({ scene, registry }: ItemTilePulsePoolConfig): TileVfxPool {
  // Shared geometry — disposed exactly once in pool.dispose()
  const sharedGeometry = new THREE.SphereGeometry(
    SPHERE_RADIUS,
    SPHERE_WIDTH_SEG,
    SPHERE_HEIGHT_SEG,
  );

  // Per-emitter state map: emitter → { handle, finished }
  const emitterStates = new Map<THREE.Object3D, EmitterState>();

  let disposed = false;

  // ── acquire ────────────────────────────────────────────────────────────────

  function acquire(args: TileVfxSpawnArgs): THREE.Object3D | null {
    if (disposed) return null;

    // Enforce max-active cap
    if (emitterStates.size >= MAX_ACTIVE) return null;

    // Per-emitter material clone (additive blending — warm blue glow on any background)
    const material = new THREE.MeshBasicMaterial({
      color: ITEM_PULSE_COLOR,
      transparent: true,
      opacity: 0.8,
      blending: THREE.AdditiveBlending,
      depthWrite: false,
    });

    const mesh = new THREE.Mesh(sharedGeometry, material);
    mesh.position.set(args.position.x, args.position.y, args.position.z);
    // Start at trough scale
    mesh.scale.setScalar(SCALE_TROUGH);

    scene.add(mesh);

    // Build gsap timeline via registry
    const handle = registry.create('item_tile_pulse');
    const tl = handle.timeline;

    const state: EmitterState = { handle, finished: false };

    // Proxy object for scale value — drives mesh.scale uniformly
    const scaleProxy = { value: SCALE_TROUGH };

    // segment 1 (0→200ms): trough → peak
    tl.to(
      scaleProxy,
      {
        value: SCALE_PEAK,
        duration: SEG1_DUR,
        ease: 'none',
        onUpdate: () => {
          mesh.scale.setScalar(scaleProxy.value);
        },
      },
      SEG1_START,
    );

    // segment 2 (200→400ms): peak → trough
    // At t=350ms: 75% through → scale = 1.4 - 0.75*0.4 = 1.10 (strictly in (1.0,1.4))
    tl.to(
      scaleProxy,
      {
        value: SCALE_TROUGH,
        duration: SEG2_DUR,
        ease: 'none',
        onUpdate: () => {
          mesh.scale.setScalar(scaleProxy.value);
        },
      },
      SEG2_START,
    );

    // segment 3 (400→560ms): trough → peak
    tl.to(
      scaleProxy,
      {
        value: SCALE_PEAK,
        duration: SEG3_DUR,
        ease: 'none',
        onUpdate: () => {
          mesh.scale.setScalar(scaleProxy.value);
        },
      },
      SEG3_START,
    );

    // segment 4 (560→700ms): peak → trough
    tl.to(
      scaleProxy,
      {
        value: SCALE_TROUGH,
        duration: SEG4_DUR,
        ease: 'none',
        onUpdate: () => {
          mesh.scale.setScalar(scaleProxy.value);
        },
      },
      SEG4_START,
    );

    // Completion callback — use tl.call(fn, undefined, absolutePos) per spec.
    // Do NOT use eventCallback('onComplete') — see gsap-registry.ts VIRTUAL_DURATION note.
    tl.call(
      () => {
        state.finished = true;
      },
      undefined,
      LIFETIME,
    );

    emitterStates.set(mesh, state);
    return mesh;
  }

  // ── update ─────────────────────────────────────────────────────────────────
  // The registry's update(dt) drives ALL timelines. This method returns the
  // current active set. Caller (manager) advances dt via registry.update().

  function update(_deltaSeconds: number): ReadonlySet<THREE.Object3D> {
    return new Set(emitterStates.keys());
  }

  // ── isFinished ─────────────────────────────────────────────────────────────

  function isFinished(emitter: THREE.Object3D): boolean {
    const state = emitterStates.get(emitter);
    if (!state) return true; // not tracked — treat as finished
    return state.finished;
  }

  // ── release ────────────────────────────────────────────────────────────────

  function release(emitter: THREE.Object3D): void {
    const state = emitterStates.get(emitter);
    if (!state) return; // safe no-op

    state.handle.dispose(); // kills timeline, removes from registry

    emitterStates.delete(emitter);

    scene.remove(emitter);

    // Dispose per-emitter material
    const mesh = emitter as THREE.Mesh;
    if (mesh.material instanceof THREE.Material) {
      mesh.material.dispose();
    }
  }

  // ── dispose ────────────────────────────────────────────────────────────────
  // Forward-defense F4: iterates active emitters and frees ALL GPU resources
  // WITHOUT expecting prior release() calls. Idempotent.

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    for (const [emitter, state] of emitterStates) {
      state.handle.dispose(); // kill timeline

      scene.remove(emitter);

      // Dispose per-emitter material
      const mesh = emitter as THREE.Mesh;
      if (mesh.material instanceof THREE.Material) {
        mesh.material.dispose();
      }
    }
    emitterStates.clear();

    // Dispose shared geometry exactly once
    sharedGeometry.dispose();
  }

  return { acquire, update, isFinished, release, dispose };
}

// ─────────────────────────────────────────────────────────────────────────────
// Event resolver
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Maps a `LandingEvent` on an `item`-type tile to item tile pulse spawn args.
 *
 * Returns null for:
 *   - any non-landing event
 *   - landing events where event.tileType !== 'item'
 *
 * Position note: LandingEvent (src/shared/game/types.ts:363-368) carries
 * `nodeId` and `tileType` but NO world-position. The position placeholder
 * {x:0, y:0, z:0} is intentional — the wire-up site (Task 5.1) resolves
 * nodeId→world-pos and passes the real position via manager.spawn(). This
 * follows the same pattern as resolveEventTileTriggerForEvent (Task 3.3).
 */
export function resolveItemTilePulseForEvent(
  event: GameEvent,
): { poolId: 'item_tile_pulse'; args: TileVfxSpawnArgs } | null {
  if (event.type !== 'landing') return null;
  if (event.tileType !== 'item') return null;

  return {
    poolId: 'item_tile_pulse',
    args: {
      position: { x: 0, y: 0, z: 0 }, // wire-up (Task 5.1) supplies real position
      tileType: 'item',
    },
  };
}
